import React, { useState } from "react"
import { Filter, TrendingUp, Brain, Check } from "lucide-react"

const FlashcardsPage = ({
	currentLanguage,
	currentFlashcards,
	handleFlashcardSelect,
	handleTabClick,
	setShowLanguageMenu,
	knownWords,
	knownWordsCount,
	totalFlashcards,
}) => {
	const [filterType, setFilterType] = useState("all") // "all", "unknown", "known"
	const [selectedCategory, setSelectedCategory] = useState("all")

	// Get unique categories
	const categories = [...new Set(currentFlashcards.map(card => card.category))]

	// Filter flashcards based on filter type and category
	const getFilteredFlashcards = () => {
		let filtered = currentFlashcards

		// Filter by known/unknown status
		if (filterType === "unknown") {
			filtered = filtered.filter(card => !knownWords.has(card.id))
		} else if (filterType === "known") {
			filtered = filtered.filter(card => knownWords.has(card.id))
		}

		// Filter by category
		if (selectedCategory !== "all") {
			filtered = filtered.filter(card => card.category === selectedCategory)
		}

		// Sort unknown words first
		return filtered.sort((a, b) => {
			const aKnown = knownWords.has(a.id)
			const bKnown = knownWords.has(b.id)
			if (aKnown === bKnown) return 0
			return aKnown ? 1 : -1
		})
	}

	const filteredFlashcards = getFilteredFlashcards()
	const progressPercentage =
		totalFlashcards > 0
			? Math.round((knownWordsCount / totalFlashcards) * 100)
			: 0

	const getFilterCount = type => {
		if (type === "unknown") {
			return currentFlashcards.filter(card => !knownWords.has(card.id)).length
		} else if (type === "known") {
			return knownWordsCount
		}
		return totalFlashcards
	}

	return (
		<div className='py-4'>
			{/* Header */}
			<div className='flex justify-between items-center mb-6'>
				<h2 className='text-xl font-bold'>
					Flashcards - {currentLanguage.name}
				</h2>
				<button
					className='text-[#6E6E6E] hover:text-white transition-colors'
					onClick={() => handleTabClick("home")}>
					Back to Home
				</button>
			</div>

			{/* Progress Overview */}
			<div className='bg-[#1E1E1E] rounded-lg p-6 mb-6'>
				<div className='flex items-center justify-between mb-4'>
					<h3 className='text-white font-semibold flex items-center'>
						<TrendingUp size={20} className='mr-2 text-[#FF3A6E]' />
						Learning Progress
					</h3>
					<div className='text-[#FF3A6E] font-bold text-lg'>
						{progressPercentage}%
					</div>
				</div>

				{/* Progress Bar */}
				<div className='mb-4'>
					<div className='bg-[#2C2C2C] rounded-full h-3 overflow-hidden'>
						<div
							className='bg-gradient-to-r from-[#FF3A6E] to-[#FF6D4B] h-full transition-all duration-500'
							style={{ width: `${progressPercentage}%` }}></div>
					</div>
				</div>

				{/* Stats */}
				<div className='grid grid-cols-3 gap-4 text-center'>
					<div className='bg-[#2C2C2C] rounded-lg p-3'>
						<div className='text-white text-2xl font-bold'>
							{knownWordsCount}
						</div>
						<div className='text-[#A0A0A0] text-sm'>Known Words</div>
					</div>
					<div className='bg-[#2C2C2C] rounded-lg p-3'>
						<div className='text-white text-2xl font-bold'>
							{getFilterCount("unknown")}
						</div>
						<div className='text-[#A0A0A0] text-sm'>To Learn</div>
					</div>
					<div className='bg-[#2C2C2C] rounded-lg p-3'>
						<div className='text-white text-2xl font-bold'>
							{totalFlashcards}
						</div>
						<div className='text-[#A0A0A0] text-sm'>Total Cards</div>
					</div>
				</div>
			</div>

			{/* Filter Controls */}
			<div className='bg-[#1E1E1E] rounded-lg p-4 mb-6'>
				<div className='flex items-center gap-2 mb-4'>
					<Filter size={16} className='text-[#FF3A6E]' />
					<span className='text-white font-medium'>Filter Cards</span>
				</div>

				{/* Status Filter */}
				<div className='flex flex-wrap gap-2 mb-4'>
					<button
						className={`px-3 py-2 rounded-lg text-sm transition-colors ${
							filterType === "all"
								? "bg-[#FF3A6E] text-white"
								: "bg-[#2C2C2C] text-[#A0A0A0] hover:bg-[#3C3C3C]"
						}`}
						onClick={() => setFilterType("all")}>
						All Cards ({totalFlashcards})
					</button>
					<button
						className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center ${
							filterType === "unknown"
								? "bg-orange-600 text-white"
								: "bg-[#2C2C2C] text-[#A0A0A0] hover:bg-[#3C3C3C]"
						}`}
						onClick={() => setFilterType("unknown")}>
						<Brain size={14} className='mr-1' />
						Need Practice ({getFilterCount("unknown")})
					</button>
					<button
						className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center ${
							filterType === "known"
								? "bg-green-600 text-white"
								: "bg-[#2C2C2C] text-[#A0A0A0] hover:bg-[#3C3C3C]"
						}`}
						onClick={() => setFilterType("known")}>
						<Check size={14} className='mr-1' />
						Known ({knownWordsCount})
					</button>
				</div>

				{/* Category Filter */}
				{categories.length > 1 && (
					<div>
						<label className='text-[#A0A0A0] text-sm mb-2 block'>
							Category:
						</label>
						<select
							className='bg-[#2C2C2C] text-white rounded-lg px-3 py-2 border-none outline-none'
							value={selectedCategory}
							onChange={e => setSelectedCategory(e.target.value)}>
							<option value='all'>All Categories</option>
							{categories.map(category => (
								<option key={category} value={category}>
									{category}
								</option>
							))}
						</select>
					</div>
				)}
			</div>

			{/* Flashcards Grid */}
			<div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
				{filteredFlashcards.length > 0 ? (
					filteredFlashcards.map(card => {
						const isKnown = knownWords.has(card.id)
						return (
							<div
								key={card.id}
								className={`relative p-4 rounded-lg cursor-pointer transition-all hover:scale-105 ${
									isKnown
										? "bg-gradient-to-br from-green-900/30 to-green-800/30 border border-green-600/30"
										: "bg-[#1E1E1E] hover:bg-[#2C2C2C] border border-transparent"
								}`}
								onClick={() => handleFlashcardSelect(card)}>
								{/* Known indicator */}
								{isKnown && (
									<div className='absolute top-2 right-2 bg-green-600 text-white rounded-full p-1'>
										<Check size={12} />
									</div>
								)}

								{/* Card content */}
								<div className='text-white font-medium mb-2'>{card.front}</div>
								<div className='text-[#A0A0A0] text-sm mb-2'>
									{card.category}
								</div>

								{/* Progress indicator */}
								<div className='flex items-center justify-between text-xs'>
									<span
										className={`px-2 py-1 rounded-full ${
											isKnown
												? "bg-green-600/20 text-green-400"
												: "bg-orange-600/20 text-orange-400"
										}`}>
										{isKnown ? "Known" : "Learning"}
									</span>
									{card.difficulty && (
										<span className='text-[#6E6E6E]'>{card.difficulty}</span>
									)}
								</div>
							</div>
						)
					})
				) : (
					<div className='col-span-3 text-center py-10'>
						{currentFlashcards.length === 0 ? (
							<>
								<div className='text-[#6E6E6E] mb-4'>
									No flashcards available for {currentLanguage.name} yet
								</div>
								<button
									className='bg-gradient-to-r from-[#FF3A6E] to-[#FF6D4B] rounded-lg p-3 px-6 text-white font-semibold inline-block'
									onClick={() => setShowLanguageMenu(true)}>
									Try Another Language
								</button>
							</>
						) : (
							<>
								<div className='text-[#6E6E6E] mb-4'>
									{filterType === "known" &&
										"No known words yet. Keep practicing!"}
									{filterType === "unknown" &&
										"Great job! You've learned all the words in this filter."}
									{filterType === "all" &&
										selectedCategory !== "all" &&
										`No cards in "${selectedCategory}" category.`}
								</div>
								<div className='flex justify-center gap-3'>
									{filterType !== "all" && (
										<button
											className='bg-[#2C2C2C] text-white py-2 px-4 rounded-lg hover:bg-[#3C3C3C]'
											onClick={() => setFilterType("all")}>
											Show All Cards
										</button>
									)}
									{selectedCategory !== "all" && (
										<button
											className='bg-[#2C2C2C] text-white py-2 px-4 rounded-lg hover:bg-[#3C3C3C]'
											onClick={() => setSelectedCategory("all")}>
											All Categories
										</button>
									)}
								</div>
							</>
						)}
					</div>
				)}
			</div>

			{/* Study Tips */}
			{filteredFlashcards.length > 0 && (
				<div className='bg-[#1E1E1E] rounded-lg p-4 mt-6'>
					<h3 className='text-white font-semibold mb-3'>💡 Study Tips</h3>
					<ul className='text-[#A0A0A0] text-sm space-y-2'>
						<li className='flex items-start'>
							<span className='text-[#FF3A6E] mr-2'>•</span>
							<span>Focus on "Need Practice" cards to improve faster</span>
						</li>
						<li className='flex items-start'>
							<span className='text-[#FF3A6E] mr-2'>•</span>
							<span>Review known words occasionally to maintain retention</span>
						</li>
						<li className='flex items-start'>
							<span className='text-[#FF3A6E] mr-2'>•</span>
							<span>Try to think of the answer before flipping the card</span>
						</li>
						<li className='flex items-start'>
							<span className='text-[#FF3A6E] mr-2'>•</span>
							<span>Study consistently for better long-term retention</span>
						</li>
					</ul>
				</div>
			)}
		</div>
	)
}

export default FlashcardsPage
